/**
 * Parse user-entered coordinates or a Google Maps link into a [lng, lat] pair.
 *
 * Accepts:
 *   - raw coordinates: "53.886908, 18.631348" (with or without the space)
 *   - Google Maps "?q=" links: "https://www.google.com/maps?q=53.886908,18.631348"
 *   - Google Maps "@lat,lng" links: "https://www.google.com/maps/@53.886908,18.631348,15z"
 *
 * Returns [lng, lat] (matching the clickPosition ordering used by MapManager),
 * or null when the input cannot be parsed or is out of range.
 */
export function parseLatLng(input: string): [number, number] | null {
  const trimmed = input.trim()
  if (!trimmed) return null

  // Prefer the "q=" query parameter when present (Google Maps share links).
  let candidate = trimmed
  const qMatch = trimmed.match(/[?&]q=([^&]+)/i)
  if (qMatch) {
    candidate = decodeURIComponent(qMatch[1])
  } else {
    // Fall back to the "@lat,lng" segment used in some Maps URLs.
    const atMatch = trimmed.match(/@(-?\d+\.?\d*\s*,\s*-?\d+\.?\d*)/)
    if (atMatch) {
      candidate = atMatch[1]
    }
  }

  const coordMatch = candidate.match(/(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)/)
  if (!coordMatch) return null

  const lat = Number(coordMatch[1])
  const lng = Number(coordMatch[2])
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  if (lat < -90 || lat > 90) return null
  if (lng < -180 || lng > 180) return null

  return [lng, lat]
}
